import { notifyManager, type QueryClient } from '@tanstack/react-query';

// React 19 never resets its nestedUpdateCount for SyncLane commits (only concurrent
// updates reset it). On page refresh, each react-query resolution triggers a SyncLane
// commit via useSyncExternalStore's forceStoreRerender. When many queries resolve in
// quick succession, the counter exceeds React's 50-update limit. Coalescing all
// notifications scheduled in the same event loop tick into a single setTimeout reduces
// the number of commits React processes before any concurrent work can reset the counter.
export const installCoalescingScheduler = () => {
  let pendingCallbacks: (() => void)[] = [];
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  notifyManager.setScheduler(callback => {
    pendingCallbacks.push(callback);
    timeoutId ??= setTimeout(() => {
      timeoutId = undefined;
      const callbacks = pendingCallbacks;
      pendingCallbacks = [];
      for (const callback_ of callbacks) callback_();
    }, 0);
  });
};

const MAX_RETRY_COUNT = 3;
const RETRYABLE_4XX_STATUS_CODES = new Set([
  408, // Request Timeout
  429, // Too Many Requests
]);

export const getErrorStatus = (error: unknown): number | undefined =>
  error instanceof Error && 'status' in error ? (error as { status?: number }).status : undefined;

export const retry = (failureCount: number, error: unknown): boolean => {
  const status = getErrorStatus(error);
  if (status && status >= 400 && status < 500 && !RETRYABLE_4XX_STATUS_CODES.has(status)) {
    return false;
  }
  return failureCount < MAX_RETRY_COUNT;
};

// Authenticated binary responses (thumbnails) are cached as blob object URLs, which the
// browser holds for the lifetime of the document unless they are revoked. A large report
// mounts thousands of them, so the tab keeps every thumbnail it has ever shown. Revoking
// when the query cache drops the entry ties the blob's lifetime to the cached value.
export const installObjectUrlRevoker = (queryClient: QueryClient) =>
  queryClient.getQueryCache().subscribe(event => {
    if (event.type !== 'removed') return;
    const cachedValue = event.query.state.data;
    if (typeof cachedValue === 'string' && cachedValue.startsWith('blob:')) {
      URL.revokeObjectURL(cachedValue);
    }
  });
